import React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import { Table, type Column } from '../../../components/ui/Table';
import type { ComplianceDocument } from '../../../types/api';
import { formatDateShort } from '../../../utils/formatters';
import {
  isTaskTerminal,
  useTaskProcessingStore,
} from '../../../stores/taskProcessing.store';
import { formatComplianceTypeLabel } from '../lib/labels';
import { ComplianceExpiryBadge } from './ComplianceExpiryBadge';
import { ComplianceOcrStatusBadge } from './ComplianceOcrStatusBadge';
import { ComplianceStatusBadge } from './ComplianceStatusBadge';

const TypeCell = styled.div`
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
`;

const ExpiryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  color: var(--text-primary);
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  flex-wrap: wrap;
`;

const readTaskDocumentId = (
  details?: Record<string, unknown>,
): string | null => {
  if (!details) {
    return null;
  }
  const value = details.documentId;
  return typeof value === 'string' ? value : null;
};

interface ComplianceTableProps {
  documents: ComplianceDocument[];
  loading: boolean;
  onView: (document: ComplianceDocument) => void;
  onDownload: (document: ComplianceDocument) => void;
  downloadingId: string | null;
}

export const ComplianceTable = ({
  documents,
  loading,
  onView,
  onDownload,
  downloadingId,
}: ComplianceTableProps) => {
  const tasks = useTaskProcessingStore((state) => state.tasks);
  const processingDocumentIds = React.useMemo(() => {
    const pending = new Set<string>();
    Object.values(tasks).forEach((task) => {
      if (!task || task.subtype !== 'compliance') {
        return;
      }
      if (isTaskTerminal(task)) {
        return;
      }
      const documentId = readTaskDocumentId(task.details);
      if (documentId) {
        pending.add(documentId);
      }
    });
    return pending;
  }, [tasks]);

  const columns: Column<ComplianceDocument>[] = [
    {
      key: 'type',
      header: 'Tipo',
      render: (_value, document) => (
        <TypeCell>{formatComplianceTypeLabel(document.type)}</TypeCell>
      ),
    },
    {
      key: 'issuing_authority',
      header: 'Autoridad',
      render: (value: string) => value,
    },
    {
      key: 'jurisdiction',
      header: 'Jurisdicción',
      render: (value: string) => value,
    },
    {
      key: 'expiry_date',
      header: 'Vencimiento',
      render: (_value, document) => (
        <ExpiryCell>
          {formatDateShort(document.expiry_date)}
          <ComplianceExpiryBadge
            expired={document.expired}
            expiringSoon={document.expiring_soon}
            daysToExpiry={document.days_to_expiry ?? null}
          />
        </ExpiryCell>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (_value, document) => (
        <ComplianceStatusBadge status={document.status} />
      ),
    },
    {
      key: 'ocr_status',
      header: 'OCR',
      render: (_value, document) => {
        const effectiveStatus = processingDocumentIds.has(document.id)
          ? 'pending'
          : document.ocr_status;
        return <ComplianceOcrStatusBadge status={effectiveStatus} />;
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_value, document) => (
        <ActionGroup>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onView(document)}
            aria-label={`Ver documento ${document.type}`}
          >
            <Icon name="eye" size="xs" aria-hidden />
            Ver
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => onDownload(document)}
            disabled={!document.storage_key}
            loading={downloadingId === document.id}
            aria-label={`Descargar documento ${document.type}`}
          >
            <Icon name="download" size="xs" aria-hidden />
            Descargar
          </Button>
        </ActionGroup>
      ),
    },
  ];

  return (
    <Table
      data={documents}
      columns={columns}
      keyExtractor={(document) => document.id}
      loading={loading}
      emptyMessage="No se encontraron documentos de cumplimiento."
      aria-label="Tabla de documentos de cumplimiento"
    />
  );
};

ComplianceTable.displayName = 'ComplianceTable';
